/**
 * Chat History Cache Utility
 * 
 * Implements a smart caching strategy for chat history to prevent
 * excessive API calls when rapidly switching between chats.
 * 
 * Features:
 * - localStorage-based cache with TTL
 * - Cache-first strategy with background refresh
 * - Automatic cache invalidation
 */

const CACHE_PREFIX = 'chat_history_';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_ENTRIES = 50; // Limit to prevent localStorage bloat

/**
 * Get cached chat history if available and not expired
 */
export const getCachedHistory = (permitChatId) => {
    try {
        const cacheKey = `${CACHE_PREFIX}${permitChatId}`;
        const cached = localStorage.getItem(cacheKey);

        if (!cached) return null;

        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;

        // Return cached data if within TTL
        if (age < CACHE_TTL) {
            return {
                data,
                isStale: age > CACHE_TTL / 2, // Mark as stale if older than 2.5 minutes
                age
            };
        }

        // Expired - remove from cache
        localStorage.removeItem(cacheKey);
        return null;
    } catch (error) {
        console.warn('Failed to read chat history cache:', error);
        return null;
    }
};

/**
 * Cache chat history
 */
export const setCachedHistory = (permitChatId, messages) => {
    try {
        const cacheKey = `${CACHE_PREFIX}${permitChatId}`;
        const cacheData = {
            data: messages,
            timestamp: Date.now()
        };

        localStorage.setItem(cacheKey, JSON.stringify(cacheData));

        // Cleanup old cache entries if needed
        cleanupOldCacheEntries();
    } catch (error) {
        console.warn('Failed to cache chat history:', error);
        // If localStorage is full, try to clear old entries and retry
        if (error.name === 'QuotaExceededError') {
            clearAllChatCache();
            try {
                const cacheKey = `${CACHE_PREFIX}${permitChatId}`;
                localStorage.setItem(cacheKey, JSON.stringify({
                    data: messages,
                    timestamp: Date.now()
                }));
            } catch (retryError) {
                console.error('Failed to cache even after cleanup:', retryError);
            }
        }
    }
};

/**
 * Invalidate cache for a specific chat
 */
export const invalidateChatCache = (permitChatId) => {
    try {
        const cacheKey = `${CACHE_PREFIX}${permitChatId}`;
        localStorage.removeItem(cacheKey);
    } catch (error) {
        console.warn('Failed to invalidate cache:', error);
    }
};

/**
 * Clear all chat history caches
 */
export const clearAllChatCache = () => {
    try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith(CACHE_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.warn('Failed to clear chat cache:', error);
    }
};

/**
 * Cleanup old cache entries to prevent localStorage bloat
 */
const cleanupOldCacheEntries = () => {
    try {
        const keys = Object.keys(localStorage);
        const chatCacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));

        if (chatCacheKeys.length <= MAX_CACHE_ENTRIES) return;

        // Get all cache entries with timestamps
        const entries = chatCacheKeys.map(key => {
            try {
                const cached = localStorage.getItem(key);
                if (!cached) return null;
                const { timestamp } = JSON.parse(cached);
                return { key, timestamp };
            } catch {
                return null;
            }
        }).filter(Boolean);

        // Sort by timestamp (oldest first)
        entries.sort((a, b) => a.timestamp - b.timestamp);

        // Remove oldest entries
        const entriesToRemove = entries.length - MAX_CACHE_ENTRIES;
        for (let i = 0; i < entriesToRemove; i++) {
            localStorage.removeItem(entries[i].key);
        }
    } catch (error) {
        console.warn('Failed to cleanup cache entries:', error);
    }
};

/**
 * Prefetch and cache chat history for better UX
 */
export const prefetchChatHistory = async (permitChatId, fetchFn) => {
    try {
        const cached = getCachedHistory(permitChatId);
        if (cached && !cached.isStale) {
            return; // Already cached and fresh
        }

        // Fetch in background and cache
        const messages = await fetchFn();
        setCachedHistory(permitChatId, messages);
    } catch (error) {
        console.warn('Failed to prefetch chat history:', error);
    }
};

